import { fetchClient } from "@/api/api";
import type {
	GeneralApiError,
	SenderPriorityUsage,
	SenderUpdateInput,
	batchCreateSendersInput,
	batchCreateSendersOutput,
	createSenderGroupInput,
	createSenderGroupOutput,
	createSenderInput,
	createSenderOutput,
	deleteMultipleSendersInput,
	deleteMultipleSendersOutput,
	deleteSenderGroupInput,
	deleteSenderGroupOutput,
	getOneSenderInput,
	getOneSenderOutput,
	getSenderGroupInput,
	getSenderGroupOutput,
	getSenderGroupsInput,
	getSenderGroupsOutput,
	getSenderHistoryInput,
	getSenderHistoryOutput,
	getSenderTransferListInput,
	getSenderTransferListOutput,
	getSenderTransferRecordsInput,
	getSenderTransferRecordsOutput,
	getSendersFacetsOutput,
	getSendersInput,
	getSendersOutput,
	requestForBatchReloginInput,
	requestForBatchReloginOutput,
	requestForReloginInput,
	requestForReloginOutput,
	stopResumeMultipleSendersInput,
	stopResumeMultipleSendersOutput,
	updateSenderGroupInput,
	updateSenderGroupOutput,
	updateSenderInfoInput,
	updateSenderInfoOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { createFacetSource } from "@/services/facets";
import { GENERAL_SERVICE_KEYS } from "@/services/general/keys";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { SENDER_SERVICE_KEYS } from "./keys";

const invalidateSendersQueries = () => {
	queryClient.invalidateQueries({
		queryKey: SENDER_SERVICE_KEYS.useGetSenders(),
	});
	queryClient.invalidateQueries({
		queryKey: SENDER_SERVICE_KEYS.useGetSenderById(),
	});
	queryClient.invalidateQueries({
		queryKey: SENDER_SERVICE_KEYS.useGetSenderStatusHistory(),
	});
	queryClient.invalidateQueries({
		queryKey: SENDER_SERVICE_KEYS.useGetSendersFacets(),
	});
};

const useGetSenders: CustomQueryHook<getSendersOutput, getSendersInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenders(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/senders/",
				{
					params: { query: payload?.query },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useCreateSingleSender: CustomMutationHook<
	createSenderInput,
	createSenderOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useCreateSingleSender(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSenders(),
			});
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSendersFacets(),
			});
		},
	});
};

const useCreateBatchSenders: CustomMutationHook<
	batchCreateSendersInput,
	batchCreateSendersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useCreateBatchSenders(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/batch/",
				{
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSenders(),
			});
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSendersFacets(),
			});
		},
		onError: toastApiError,
	});
};

const getSenderById = async (
	payload: getOneSenderInput,
): Promise<getOneSenderOutput> => {
	const { data, error } = await fetchClient.GET(
		"/v1/marketplace/senders/{sender_id}",
		{
			params: { path: payload.path },
			throwOnError: true,
		},
	);
	if (error) throw error;
	return data;
};

const getSendersDetails = async (
	senderIds: string[],
): Promise<getOneSenderOutput[]> => {
	const { data, error } = await fetchClient.POST(
		"/v1/marketplace/senders/details/",
		{ body: senderIds, throwOnError: true },
	);
	if (error) throw error;
	return data;
};

const useGetSenderById: CustomQueryHook<getOneSenderOutput, getOneSenderInput> =
	(payload, options) => {
		return useQuery({
			...options,
			queryKey: SENDER_SERVICE_KEYS.useGetSenderById(payload),
			queryFn: () => getSenderById(payload),
		});
	};

const useGetSenderStatusHistory: CustomQueryHook<
	getSenderHistoryOutput,
	getSenderHistoryInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenderStatusHistory(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/senders/{sender_id}/history/",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetSenderTransferRecord: CustomQueryHook<
	getSenderTransferRecordsOutput,
	getSenderTransferRecordsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenderTransferRecord(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/senders/{sender_id}/transfer-records/",
				{
					params: { path: payload.path, query: payload.query },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetSenderTransferList: CustomQueryHook<
	getSenderTransferListOutput,
	getSenderTransferListInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenderTransferList(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/senders/{sender_id}/transfer-list/",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useDeleteMultipleSenders: CustomMutationHook<
	deleteMultipleSendersInput,
	deleteMultipleSendersOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();
	const navigate = useNavigate();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useDeleteMultipleSenders(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/delete-many/",
				{
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("sender.action.successDeleteSenders"));
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSenders(),
			});
			queryClient.invalidateQueries({
				queryKey: SENDER_SERVICE_KEYS.useGetSendersFacets(),
			});
			navigate({ to: "/senders" });
		},
	});
};

const useStopResumeMultipleSenders: CustomMutationHook<
	stopResumeMultipleSendersInput,
	stopResumeMultipleSendersOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useStopResumeMultipleSenders(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/stop-resume/",
				{
					params: { query: payload.query },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			invalidateSendersQueries();
		},
	});
};

const useUpdateSender: CustomMutationHook<
	updateSenderInfoInput,
	updateSenderInfoOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useUpdateSender(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/senders/{sender_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			invalidateSendersQueries();
		},
	});
};

const useAssignSenderGroup = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useAssignSenderGroup(),
		mutationFn: async ({
			senderIds,
			senderGroupId,
		}: {
			senderIds: string[];
			senderGroupId: SenderUpdateInput["sender_group_id"];
		}) => {
			const { error } = await fetchClient.POST(
				"/v1/marketplace/senders/update-many/",
				{
					body: { sender_ids: senderIds, sender_group_id: senderGroupId },
					throwOnError: true,
				},
			);
			if (error) throw error;
		},
		onError: toastApiError,
		onSuccess: invalidateSendersQueries,
	});
};

const useBatchUpdateSenderPriority = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		void,
		GeneralApiError,
		{ senderIds: string[]; priorityUsage: SenderPriorityUsage | null }
	>({
		mutationKey: SENDER_SERVICE_KEYS.useBatchUpdateSenderPriority(),
		mutationFn: async ({ senderIds, priorityUsage }) => {
			const { error } = await fetchClient.POST(
				"/v1/marketplace/senders/update-many/",
				{
					body: { sender_ids: senderIds, priority_usage: priorityUsage },
					throwOnError: true,
				},
			);
			if (error) throw error;
		},
		onError: toastApiError,
		onSuccess: invalidateSendersQueries,
	});
};

const useReloginForOneSender: CustomMutationHook<
	requestForReloginInput,
	requestForReloginOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useReloginForOneSender(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/{sender_id}/relogin/",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			invalidateSendersQueries();
		},
	});
};

const useReloginMultipleSenders: CustomMutationHook<
	requestForBatchReloginInput,
	requestForBatchReloginOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useReloginMultipleSenders(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/senders/relogin/",
				{
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			invalidateSendersQueries();
		},
	});
};

const invalidateSenderGroupQueries = () => {
	queryClient.invalidateQueries({
		queryKey: SENDER_SERVICE_KEYS.useGetSenderGroups(),
	});
	queryClient.invalidateQueries({
		queryKey: GENERAL_SERVICE_KEYS.useGetAllGroups(),
	});
};

const useGetSenderGroups: CustomQueryHook<
	getSenderGroupsOutput,
	getSenderGroupsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenderGroups(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/sender-groups/",
				{
					params: { query: payload?.query },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetSenderGroup: CustomQueryHook<
	getSenderGroupOutput,
	getSenderGroupInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SENDER_SERVICE_KEYS.useGetSenderGroup(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/sender-groups/{group_id}",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useCreateSenderGroup: CustomMutationHook<
	createSenderGroupInput,
	createSenderGroupOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useCreateSenderGroup(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/sender-groups/",
				{
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("senderGroup.createSuccess"));
			invalidateSenderGroupQueries();
		},
	});
};

const useUpdateSenderGroup: CustomMutationHook<
	updateSenderGroupInput,
	updateSenderGroupOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useUpdateSenderGroup(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/sender-groups/{group_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("senderGroup.updateSuccess"));
			invalidateSenderGroupQueries();
			invalidateSendersQueries();
		},
	});
};

const useDeleteSenderGroup: CustomMutationHook<
	deleteSenderGroupInput,
	deleteSenderGroupOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: SENDER_SERVICE_KEYS.useDeleteSenderGroup(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/marketplace/sender-groups/{group_id}",
				{
					params: { path: payload.path },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("senderGroup.deleteSuccess"));
			invalidateSenderGroupQueries();
		},
	});
};

const useDeleteSenderGroups = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, GeneralApiError, string[]>({
		mutationKey: SENDER_SERVICE_KEYS.useDeleteSenderGroups(),
		mutationFn: async (groupIds) => {
			const { error } = await fetchClient.POST(
				"/v1/marketplace/sender-groups/delete-many/",
				{ body: groupIds, throwOnError: true },
			);
			if (error) throw error;
		},
		onError: toastApiError,
		onSuccess: () => {
			invalidateSenderGroupQueries();
			invalidateSendersQueries();
		},
	});
};

const SENDERS_FACET_SOURCE = createFacetSource<getSendersFacetsOutput>({
	path: "/v1/marketplace/senders/facets/",
	queryKey: (payload) =>
		SENDER_SERVICE_KEYS.useGetSendersFacets(payload as any),
});

export {
	SENDERS_FACET_SOURCE,
	useGetSenders,
	useCreateSingleSender,
	useCreateBatchSenders,
	getSenderById,
	getSendersDetails,
	useGetSenderById,
	useGetSenderStatusHistory,
	useGetSenderTransferRecord,
	useGetSenderTransferList,
	useDeleteMultipleSenders,
	useStopResumeMultipleSenders,
	useUpdateSender,
	useAssignSenderGroup,
	useBatchUpdateSenderPriority,
	useReloginForOneSender,
	useReloginMultipleSenders,
	useGetSenderGroups,
	useGetSenderGroup,
	useCreateSenderGroup,
	useUpdateSenderGroup,
	useDeleteSenderGroup,
	useDeleteSenderGroups,
};
